<template>
  <div class="serial-code-create">
    <div class="mb-x2" layout layout-align="space-between-start">
      <el-button type="default" @click="backToSerialCodeList"><i class="mdi mdi-keyboard-backspace"></i>戻る</el-button>
    </div>
    <div class="flex-col mb-x2" v-if="campaign.type === 'old_web_instantwin'">
      <div class="types">
        <span layout>割り当てタイミング</span>
        <div>
          <el-tooltip placement="top">
            <template v-slot:content
              ><span
                v-html="
                  'WEB応募の当選ページにてシリアルコードを割り当てる場合は、WEBページを選択してください。<br>WEB応募の当選後、DM送信するタイミングでシリアルコードを割り当てる場合は、当選DMを選択してください。'
                "
              ></span
            ></template>
            <template v-slot:default>
              <div class="mdi mdi-help-circle"></div>
            </template>
          </el-tooltip>
        </div>
      </div>
      <select-form
        class="status-filter"
        :value="status.value"
        :options="status.options"
        :onChange="status.onChange"
      ></select-form>
    </div>
    <div class="message message-info mb-x2">
      シリアルコードを入力してください。<br />複数入力する場合、1シリアルコード１行となるよう改行してください。最大文字数は256文字になります。
    </div>
    <div class="input-container">
      <el-input
        class="body-textarea"
        v-model="serialCode"
        type="textarea"
        placeholder="シリアルコードを入力"
        :class="{ success: $v.serialCode.required && !$v.serialCode.$invalid }"
        @input="onChangeSerialCodeBody"
      ></el-input>
      <div class="input-invalid-text" v-if="maxSerialCodeError">
        一度にインポートできる件数（10,000件）を超えています。
      </div>
    </div>
    <div class="dialog-footer">
      <el-button type="primary" :disabled="disabled" @click="saveSerialCode"
        ><i class="mdi mdi-content-save"></i>保存</el-button
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.serial-code-create {
  .body-textarea {
    textarea {
      height: 360px;
    }
  }
}

.flex-col {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
  width: 25%;
}

.input-container {
  margin: 0 0 ($spacing-unit * 3);
}

.types {
  display: flex;
  flex-direction: row;
  gap: math.div($spacing-unit, 2);
  align-items: center;
}

.mdi.mdi-help-circle::before {
  font-size: $font-large-size;
}
</style>
<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import SelectForm from '@/client/components/_atoms/select_form'
import api from '@/client/core/api'
import { TrackingService } from '@/client/services'
import { useTwitterApplicantStore } from '@/client/store/useTwitterApplicantStore'
import { useTwitterSerialCodeDialogStore } from '@/client/store/useTwitterSerialCodeDialogStore'

export default defineComponent({
  props: {
    isChangeWithoutSubmit: Boolean
  },
  components: {
    SelectForm
  },
  data() {
    return {
      serialCode: '',
      isSubmit: false,
      status: {
        value: 'DM',
        options: [
          {
            label: '当選DM',
            value: 'DM'
          },
          {
            label: 'Webページ',
            value: 'web'
          }
        ],
        onChange: (value: 'DM' | 'web') => {
          TrackingService.sendEvent('select:当選メッセージの管理|シルアルコード追加|割当タイミング指定')

          this.status.value = value
        }
      }
    }
  },

  watch: {
    isFormChanged(value: boolean) {
      this.$emit('update:isChangeWithoutSubmit', value)
    }
  },

  created() {
    if (this.campaign.type === 'old_web_instantwin') {
      this.status.value = 'web'
    }
  },

  computed: {
    ...mapState(useTwitterSerialCodeDialogStore, ['campaignId', 'prizeId']),
    ...mapState(useTwitterApplicantStore, {
      campaign: state => state.campaign ?? {}
    }),

    isFormChanged() {
      return this.serialCode !== ''
    },

    maxSerialCodeError() {
      if (!this.serialCode.trim()) {
        return false
      }

      return this.serialCode.trim().split('\n').length > 10000
    },

    disabled() {
      return this.$v.$invalid || !this.serialCode.trim() || this.maxSerialCodeError || this.isSubmit
    }
  },
  methods: {
    ...mapActions(useTwitterSerialCodeDialogStore, ['fetchSerialCodeList', 'setSerialCodeIsCreated']),

    /**
     * 値のリセット
     */
    reset() {
      this.serialCode = ''
      this.isSubmit = false
      this.$emit('update:isChangeWithoutSubmit', false)
      this.$v.serialCode.$reset()
    },

    /**
     * シリアルコード一覧に戻る
     */
    backToSerialCodeList() {
      TrackingService.sendEvent('click:当選メッセージの管理|シルアルコード追加|戻る')

      if (!this.isChangeWithoutSubmit) {
        return this.setSerialCodeIsCreated(false)
      }

      const confirm = window.confirm('編集内容が保存されていません。ダイアログを閉じてよろしいですか?')

      if (!confirm) {
        return
      }

      this.$emit('update:isChangeWithoutSubmit', false)
      this.setSerialCodeIsCreated(false)
    },

    saveSerialCode() {
      TrackingService.sendEvent('click:当選メッセージの管理|シルアルコード追加|保存')

      if (this.isSubmit) return

      const serialCodes = this.serialCode.split('\n').filter(code => code)

      this.postButton({
        campaignId: this.campaignId,
        prizeId: this.prizeId,
        serialCodes,
        type: this.status.value
      })
    },

    /**
     * 送信完了イベント
     */
    onSubmitSuccess() {
      this.reset()
      this.fetchSerialCodeList()
      this.setSerialCodeIsCreated(false)
    },

    /**
     * シリアルコードの新規作成
     * @param {Object} params 送信データ
     */
    async postButton(params: any) {
      this.isSubmit = true

      if (params.serialCodes.some(code => code.length > 256)) {
        this.isSubmit = false

        this.$notify({
          title: '最大文字数を超えるシリアルコードが含まれています。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      const response = await api.post(`/twitter_serial_codes`, params)

      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (response.data) {
        let title = `${response.data.savedCount}件シリアルコードを保存しました。`

        if (response.data.duplicatedCount) {
          title = `${response.data.savedCount}件シリアルコードを保存しました。(重複${response.data.duplicatedCount}件)`
        }

        this.$notify({
          title,
          customClass: 'success',
          duration: 5000
        })
        this.onSubmitSuccess()
        return
      }

      this.$notify({
        title: 'シリアルコードの保存に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
    },

    onChangeSerialCodeBody() {
      TrackingService.sendEvent('input:当選メッセージの管理|シルアルコード追加|入力')
    }
  },
  validations: {
    serialCode: {
      required
    }
  }
})
</script>
